import React, {useState} from "react";
import { FileIcon, defaultStyles } from 'react-file-icon';
import {VideoPlayer} from "../widgets/AssetModal";
import {useViewMode} from "./ViewMenu";
import {isMobile} from "device-detect";
import {Badge, Tooltip} from "antd";
import {resolveAssetImageSrc} from "./resolveAssetImageSrc";
import ConfigurableEntityIcon from "../configurable_entities/ConfigurableEntityIcon";

export default ({asset, assetSize, thumbType, fileIconSize, hovering, forceGrid=false})=> {
    const {viewMode, colorLabel} = useViewMode()

    const src = resolveAssetImageSrc(asset, thumbType)

    const size = src || !fileIconSize ? assetSize : fileIconSize

    const isGrid = forceGrid || viewMode === 'grid'
    const isPosts = viewMode === 'posts'

    const style = {width: size, objectFit: 'scale-down', borderRadius: isGrid ? 10 : 0, overflow: 'hidden',
        backgroundImage: asset.ext == 'png' && `url(${ImageUrls.checkerImage})`, backgroundSize: 15}

    if(colorLabel) {
        asset.custom_meta_values?.filter(cmv => cmv.custom_meta_field_id === colorLabel && cmv.color_label?.length)?.map(cmv => {
            style.border = `8px solid ${cmv.color_label}`;
            style.backgroundColor = cmv.color_label;
        })
    }

    let aspectRatio
    if(isGrid) {
        style.height = size

    } else if(isPosts) {
        if(asset.type === 'Video') {
            // Currently video previews are locked at 4:3 aspect ratio:
            style.height = (isMobile() ? window.innerWidth : Config.defaultPostWidth) / 1.333;

        } else {
            aspectRatio = asset.width / asset.height
            style.height = (isMobile() ? window.innerWidth : Config.defaultPostWidth) / aspectRatio
        }
    }

    const extStyles = defaultStyles[asset.ext]

    // An early derivative request can fail before processing publishes it.
    // Retry on a new source or processing revision, but not on every render.
    // Keep this retry identity separate from the DOM key: refreshed URL
    // signatures and metadata must not unmount a healthy image.
    const imageKey = JSON.stringify([asset.id, src, asset.aasm_state, asset.updated_at]);
    const [failedImageKey, setFailedImageKey] = useState()
    const loadError = failedImageKey === imageKey;
    const onImgLoadError = ()=> {
        setFailedImageKey(imageKey)
    }

    const { autoPlay } = useViewMode()

    if((((autoPlay && hovering) || !isGrid) && asset.type === 'Video' && asset.small_url)) {
        return (
            <div style={{height: !isGrid ? size / 1.333 : 'auto'}}>
                <VideoPlayer
                    style={{backgroundColor:'#fff !important'}}
                    width={size}
                    height={isGrid && size}
                    muted={isGrid}
                    loop={isGrid}
                    autoplay={isGrid}
                    preload={isGrid && 'auto'}
                    inactivityTimeout={0}
                    asset={asset}
                    key={asset.id}
                    controls={!isGrid}
                    sources={ [ {type:'video/mp4', src: asset.small_url} ] }
                    className={'video-js vjs-default-skin vjs-static-controls'}
                />

            </div>
        )
    }

    if(!loadError && src) {
        const isPortrait = asset.height > asset.width;
        return (
            <div style={{...style, backgroundImage: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center'}} className={'asset-grid-image-wrapper'}>
                <img
                    key={asset.id}
                    src={src}
                    draggable={false}
                    style={{
                        width: isPortrait ? 'auto' : '100%',
                        height: isPortrait ? '100%' : 'auto',
                        objectFit: 'scale-down',
                        backgroundImage: asset.has_alpha && `url(${ImageUrls.checkerImage})`,
                        backgroundSize: 15
                    }}
                    onError={onImgLoadError}
                    alt={asset.alt_text || asset.description}
                />
            </div>
        )
    }

    const entityMetaValues = entityAssetPreviewValues(asset)
    const hasMainTextPreview = !!asset.content_preview?.trim?.()

    if(!src && asset.entity_type_id && (entityMetaValues.length || hasMainTextPreview)) {
        return (
            <div style={{
                ...style,
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: 'white',
                height: size - 5,
                padding: '.55em',
                boxSizing: 'border-box',
            }}
                className={'asset-file-icon-wrapper entity-asset-preview'}
            >
                <div style={{fontWeight: 700, marginBottom: '.35em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                    <ConfigurableEntityIcon icon={asset.entity_type?.icon} kind="text_asset"/> {asset.entity_type?.name || 'Configurable Asset'}
                </div>
                <div style={{fontSize: 12, lineHeight: 1.35}}>
                    {hasMainTextPreview && (
                        <div style={{marginBottom: '.35em'}}>
                            <strong>Main Text</strong>
                            <div style={{
                                whiteSpace: 'pre-wrap',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical'
                            }}>
                                {asset.content_preview}
                            </div>
                        </div>
                    )}
                    {entityMetaValues.slice(0, hasMainTextPreview ? 3 : 5).map(value => (
                        <div key={`${value.custom_meta_field_id}-${value.id}`} style={{display: 'flex', gap: 6}}>
                            {value.color_label && <Badge color={value.color_label} style={{flexShrink: 0, marginTop: 4}}/>}
                            <strong style={{flex: '0 0 42%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                {value.name}
                            </strong>
                            <span style={{flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                {value.preview}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    if (!src && asset.content_preview?.length)
        return (
            <div style={{
                ...style,
                position: 'relative', display: 'flex', justifyContent: 'center', overflow:'hidden', backgroundColor: 'white', height: size - 5}}
                className={'asset-file-icon-wrapper'}
            >
                <small>
                    <pre style={{whiteSpace: 'pre-wrap', textOverflow: 'ellipsis', width: size, padding:'.5em'}} className={'content-preview'}>
                        {asset.content_preview}
                    </pre>
                </small>
            </div>
        )

    return !isGrid ? (
        <div style={{...style, position:'relative', backgroundColor: 'lightgrey'}}>

        </div>
    ) : (
        <div style={{...style, position:'relative', display: 'flex', justifyContent: 'center', padding:1}} className={'asset-file-icon-wrapper'}>
            <FileIcon extension={asset.ext} {...extStyles} radius={2}/>
        </div>
    )
}

function entityAssetPreviewValues(asset) {
    return (asset.custom_meta_values || [])
        .map(value => ({
            ...value,
            preview: value.text || value.value || value.date,
        }))
        .filter(value => value.name && value.preview !== null && `${value.preview || ''}`.length)
}
